import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { DETAIL_TABLE_WIDER_DATES_HINT } from '@/components/common/detailTableLayout.constants';
import { PersonBadge } from '@/components/common/PersonBadge';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import type { ViewedRowList } from '@/store/lastViewedRow.store';
import { workSummaryQueryOptions } from '../api/dashboards.api';
import {
  getWorkSummaryDcrColumns,
  getWorkSummaryExpenseColumns,
} from '../components/WorkSummaryColumns';
import { DCR_TYPE_COLUMNS } from '../constants/dashboard.constants';
import { getWorkSummaryStatCards, type WorkSummaryTab } from '../utils/workSummaryStatCards';

const SUMMARY_NAME = 'Work Summary';
const ERROR_TITLE = 'Could not load the work summary';
const PERSON_ROLE_LABEL = 'ZM';

interface WorkSummaryPeriod {
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

interface WorkSummaryPageProps {
  userId: number;
  period: WorkSummaryPeriod;
  dcrTypeId?: number | undefined;
  isVerifiedOnly: boolean;
  viewedRowList: ViewedRowList;
  onNoHistory: () => void;
}

function getPageTitle(dcrTypeId: number | undefined): string {
  if (dcrTypeId === undefined) return SUMMARY_NAME;
  return DCR_TYPE_COLUMNS.find((column) => column.id === dcrTypeId)?.title ?? SUMMARY_NAME;
}

export function WorkSummaryPage({
  userId,
  period,
  dcrTypeId,
  isVerifiedOnly,
  viewedRowList,
  onNoHistory,
}: WorkSummaryPageProps) {
  const [activeTab, setActiveTab] = useState<WorkSummaryTab>('dcr');

  useRememberViewedRow(viewedRowList, userId);

  const summaryQuery = useQuery(
    workSummaryQueryOptions(userId, {
      dateFrom: period.dateFrom,
      dateTo: period.dateTo,
      dcrTypeId,
      isVerifiedOnly,
    }),
  );
  const summary = summaryQuery.data;
  const includesExpenses = dcrTypeId === undefined;
  const personName = summary?.profile.name ?? null;
  const dcrColumns = useMemo(
    () => getWorkSummaryDcrColumns(userId, personName),
    [userId, personName],
  );
  const expenseColumns = useMemo(() => getWorkSummaryExpenseColumns(), []);

  const tabs: TableTab<WorkSummaryTab>[] = [
    { id: 'dcr', label: 'DCR Entries', count: summary?.dcr.length },
    { id: 'expenses', label: 'Expenses', count: summary?.expenses.length },
  ];

  const sharedLayout = {
    title: getPageTitle(dcrTypeId),
    titleAddon: (
      <DateRangeBadge
        dateFrom={period.dateFrom ?? summary?.period.dateFrom}
        dateTo={period.dateTo ?? summary?.period.dateTo}
      />
    ),
    breadcrumbAddon: (
      <PersonBadge
        roleLabel={PERSON_ROLE_LABEL}
        name={summary?.profile.name}
        isLoading={summaryQuery.isLoading}
      />
    ),
    onNoHistory,
    stats: getWorkSummaryStatCards(summary?.totals, includesExpenses ? activeTab : 'dcr'),
    tableTabs: includesExpenses ? (
      <TableTabs
        tabs={tabs}
        activeTabId={activeTab}
        onTabChange={setActiveTab}
        ariaLabel="Work summary tables"
      />
    ) : undefined,
  };
  const sharedTable = {
    isLoading: summaryQuery.isLoading,
    error: summaryQuery.error,
    errorTitle: ERROR_TITLE,
    onRetry: () => void summaryQuery.refetch(),
    stickyColumnId: 'date',
    emptyHint: DETAIL_TABLE_WIDER_DATES_HINT,
  };

  if (includesExpenses && activeTab === 'expenses') {
    return (
      <DetailTableLayout
        {...sharedLayout}
        table={{
          ...sharedTable,
          rows: summary?.expenses ?? [],
          columns: expenseColumns,
          emptyMessage: 'No expenses in this period',
        }}
      />
    );
  }

  return (
    <DetailTableLayout
      {...sharedLayout}
      table={{
        ...sharedTable,
        rows: summary?.dcr ?? [],
        columns: dcrColumns,
        emptyMessage: 'No DCR entries in this period',
      }}
    />
  );
}
